import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ROLE_ID_ADMIN } from '../constants/roleIds';
import { isTokenExpired } from '../utils/jwtUtils';

interface User {
  id: number;
  role: string;
  /** Nombre legible del rol (API); preferido para UI */
  role_name?: string;
  role_id: number;
  permissions: string[];
  agente_id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  hasPermission: (permissionName: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  /**
   * Verifica si hay una sesión válida basándose en los tokens
   */
  const checkAuthentication = useCallback(() => {
    const accessToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    // Si no hay token, no está autenticado
    if (!accessToken) {
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }

    // Si el token está expirado, limpiar y marcar como no autenticado
    if (isTokenExpired(accessToken)) {
      setIsAuthenticated(false);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      return false;
    }

    // Si hay token válido y usuario en localStorage, establecer autenticación
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
        return true;
      } catch (error) {
        console.error('Error parseando usuario del localStorage:', error);
        setIsAuthenticated(false);
        return false;
      }
    }

    // Si hay token pero no hay usuario, aún puede estar autenticado
    // (el usuario se puede cargar después)
    setIsAuthenticated(true);
    return true;
  }, []);

  /**
   * Función centralizada para hacer logout
   */
  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionExpired');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    // Verificar autenticación al cargar
    checkAuthentication();

    // Escuchar cambios en localStorage (por si se modifica desde otra pestaña)
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === 'accessToken' ||
        e.key === 'refreshToken' ||
        e.key === 'user'
      ) {
        checkAuthentication();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuthentication]);

  const hasPermission = (permissionName: string) => {
    if (!user) return false;
    // Si el usuario es administrador (role_id = 1), tiene todos los permisos
    if (user.role_id === ROLE_ID_ADMIN) return true;
    return user.permissions.includes(permissionName);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        setUser,
        hasPermission,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}; 